// ══════════════════════════════════════════════════════════════════════════════
// PAGE AGENDA - Calendrier Google + Événements avec Google Meet
// AbracadaWorld - Cockpit Particulier
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Plus, ChevronLeft, ChevronRight, Video, MapPin,
  Clock, Users, X, Check, ExternalLink, Link2, RefreshCw,
  AlertCircle, Settings, LogOut, Sparkles, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import RegisterPromptModal from '../../components/RegisterPromptModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getToken = () => {
  const buyerToken = localStorage.getItem('buyer_token');
  if (buyerToken) return buyerToken;
  const clientToken = localStorage.getItem('client_token');
  if (clientToken) return clientToken;
  return localStorage.getItem('authToken');
};

// Couleurs pour les événements
const eventColors = [
  'bg-blue-500', 'bg-purple-500', 'bg-green-500', 
  'bg-orange-500', 'bg-pink-500', 'bg-teal-500'
];

export default function AgendaPage({ onAskAbra, onOpenRegisterModal, onBackToHub }) {
  const [googleStatus, setGoogleStatus] = useState({ connected: false, loading: true });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [localShowRegisterModal, setLocalShowRegisterModal] = useState(false);
  const isDarkMode = false;
  
  // Détection mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // État pour détecter le clavier mobile
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  // Détection du clavier via visualViewport API
  useEffect(() => {
    if (!showCreateModal || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    // Masquer la navbar quand le modal est ouvert
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [showCreateModal, isMobile]);
  
  const [newEvent, setNewEvent] = useState({
    summary: '',
    description: '',
    date: '',
    start_time: '09:00',
    end_time: '10:00',
    attendees: '',
    add_meet: true
  });

  // Vérifier la connexion Google
  const checkGoogleStatus = async () => {
    try {
      const token = getToken();
      if (!token) {
        setGoogleStatus({ connected: false, loading: false });
        return;
      }
      const res = await fetch(`${API_URL}/api/google/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGoogleStatus({ ...data, loading: false });
        if (data.connected) {
          loadEvents();
        }
      } else {
        setGoogleStatus({ connected: false, loading: false });
      }
    } catch (error) {
      console.error('Error checking Google status:', error);
      setGoogleStatus({ connected: false, loading: false });
    }
  };

  // Connecter Google
  const connectGoogle = async () => {
    try {
      const token = getToken();
      
      if (!token) {
        // Ouvrir le popup d'inscription via le parent
        if (onOpenRegisterModal) {
          onOpenRegisterModal('connecter ton agenda Google', true);
        } else {
          setLocalShowRegisterModal(true);
        }
        return;
      }
      
      const res = await fetch(`${API_URL}/api/google/auth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const { auth_url } = await res.json();
        window.location.href = auth_url;
      } else {
        toast.error('Erreur de connexion Google');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Déconnecter Google
  const disconnectGoogle = async () => {
    if (!window.confirm('Déconnecter ton compte Google ?')) return;
    
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/google/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleStatus({ connected: false, loading: false });
      setEvents([]);
      toast.success('Compte Google déconnecté');
    } catch (error) {
      toast.error('Erreur de déconnexion');
    }
  };

  // Charger les événements
  const loadEvents = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
      
      const res = await fetch(
        `${API_URL}/api/google/calendar/events?time_min=${startOfMonth.toISOString()}&time_max=${endOfMonth.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Créer un événement
  const createEvent = async () => {
    if (!newEvent.summary || !newEvent.date) {
      toast.error('Le titre et la date sont obligatoires');
      return;
    }

    try {
      const token = getToken();
      const startDateTime = `${newEvent.date}T${newEvent.start_time}:00`;
      const endDateTime = `${newEvent.date}T${newEvent.end_time}:00`;
      
      const attendees = newEvent.attendees
        .split(',')
        .map(e => e.trim())
        .filter(e => e.includes('@'));

      const res = await fetch(`${API_URL}/api/google/calendar/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          summary: newEvent.summary,
          description: newEvent.description,
          start_datetime: startDateTime,
          end_datetime: endDateTime,
          attendees: attendees,
          add_meet: newEvent.add_meet
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(
          newEvent.add_meet 
            ? 'Événement créé avec lien Google Meet !' 
            : 'Événement créé !'
        );
        setShowCreateModal(false);
        setNewEvent({
          summary: '',
          description: '',
          date: '',
          start_time: '09:00',
          end_time: '10:00',
          attendees: '',
          add_meet: true
        });
        loadEvents();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Supprimer un événement
  const deleteEvent = async (eventId) => {
    if (!window.confirm('Supprimer cet événement ?')) return;

    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/google/calendar/events/${eventId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Événement supprimé');
        setSelectedEvent(null);
        loadEvents();
      }
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  useEffect(() => {
    checkGoogleStatus();
    
    // Check for callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      toast.success('Compte Google connecté !');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (googleStatus.connected) {
      loadEvents();
    }
  }, [currentDate, googleStatus.connected]);

  // Navigation mois
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Générer les jours du calendrier
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay() || 7; // 1 = Lundi
    
    const days = [];
    
    // Jours du mois précédent
    for (let i = startingDay - 1; i > 0; i--) {
      const prevDate = new Date(year, month, 1 - i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Jours du mois courant
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Jours du mois suivant
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const getEventsForDay = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // État non connecté - Style harmonisé
  if (!googleStatus.loading && !googleStatus.connected) {
    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className={isMobile ? 'mb-2' : 'mb-4'}>
          <div className="flex items-center gap-2">
            {isMobile && onBackToHub && (
              <button onClick={onBackToHub} className="p-1 -ml-1" data-testid="back-to-hub-btn">
                <ArrowLeft size={20} className="text-[#1e3a5f]" strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold text-[#1e3a5f]`}>Mon agenda</h1>
              {!isMobile && <p className="text-sm text-gray-500">Organise tes journées et tes rendez-vous</p>}
            </div>
          </div>
        </div>

        {/* État non connecté */}
        <div className="flex-1 rounded-2xl bg-white border border-gray-100 flex items-center justify-center">
          <div className="text-center p-8 max-w-md">
            {/* Badge Beta */}
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
              Version Beta
            </div>
            
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-orange-100 to-orange-50">
              <Calendar size={28} className="text-orange-500" />
            </div>
            
            <h3 className="text-lg font-semibold mb-2 text-gray-900">Connecte ton Agenda</h3>
            <p className="text-sm mb-6 text-gray-500">
              Synchronise ton calendrier pour voir tes rendez-vous et créer des visios.
            </p>
            
            {/* Google Calendar - Disponible */}
            <button
              onClick={connectGoogle}
              data-testid="connect-calendar-btn"
              className="w-full py-3 rounded-xl flex items-center justify-center gap-3 transition-colors mb-3 bg-white hover:bg-gray-50 border-2 border-gray-200"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
              <span className="font-semibold text-sm text-gray-700">Google Calendar</span>
              <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Disponible</span>
            </button>
            
            {/* Outlook Calendar - Prochainement */}
            <div className="w-full py-3 rounded-xl flex items-center justify-center gap-3 mb-3 opacity-60 bg-gray-50 border-2 border-dashed border-gray-200">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7v10l10 5 10-5V7L12 2z" fill="#0078D4"/>
              </svg>
              <span className="font-semibold text-sm text-gray-500">Outlook Calendar</span>
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Bientôt</span>
            </div>
            
            {/* iCloud Calendar - Prochainement */}
            <div className="w-full py-3 rounded-xl flex items-center justify-center gap-3 opacity-60 bg-gray-50 border-2 border-dashed border-gray-200">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path d="M19.5 10.5c0-4.14-3.36-7.5-7.5-7.5S4.5 6.36 4.5 10.5c0 .17.01.34.02.5A5.5 5.5 0 006 21.5h12a5.5 5.5 0 001.5-10.8c0-.07 0-.13 0-.2z" fill="#007AFF"/>
              </svg>
              <span className="font-semibold text-sm text-gray-500">iCloud Calendar</span>
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">Bientôt</span>
            </div>
            
            <p className="mt-4 text-xs text-gray-400">Tes données restent privées et sécurisées.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style harmonisé */}
      <div className={isMobile ? 'mb-2' : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isMobile && onBackToHub && (
              <button onClick={onBackToHub} className="p-1 -ml-1" data-testid="back-to-hub-btn">
                <ArrowLeft size={20} className="text-[#1e3a5f]" strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`${isMobile ? 'text-[16px]' : 'text-xl'} font-bold text-[#1e3a5f]`}>Mon agenda</h1>
              {!isMobile && (
                <p className="text-sm text-gray-500">
                  {googleStatus.email || 'Organise tes journées et tes rendez-vous'}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={disconnectGoogle}
              className="p-2 rounded-xl bg-white border border-gray-100 hover:bg-gray-50 transition-colors"
              title="Déconnecter Google"
            >
              <LogOut size={18} className="text-gray-500" />
            </button>
            {isMobile ? (
              <button 
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F97316] text-white rounded-lg font-semibold text-[11px] whitespace-nowrap"
              >
                Nouvel événement
              </button>
            ) : (
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
              >
                <Plus size={16} />
                Nouvel événement
              </Button>
            )}
          </div>
        </div>

        {/* Navigation mois */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <Calendar size={14} className="text-orange-500" />
            <span className="text-xs font-medium text-gray-600">
              {events.length} événement{events.length > 1 ? 's' : ''} ce mois
            </span>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            <span className="px-3 py-1 text-sm font-semibold text-[#1e3a5f]">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </span>
            <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronRight size={20} className="text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendrier - dans un conteneur harmonisé */}
      <div className="flex-1 rounded-2xl bg-white border border-gray-100 overflow-hidden flex flex-col">
        {/* Calendrier */}
        <div className="p-4 flex-1 overflow-y-auto">
        {/* Jours de la semaine */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Grille du calendrier */}
        <div className="grid grid-cols-7 gap-1">
          {getDaysInMonth().map((day, index) => {
            const dayEvents = getEventsForDay(day.date);
            const isToday = day.date.toDateString() === new Date().toDateString();
            
            return (
              <div
                key={index}
                className={`min-h-[80px] p-1 rounded-lg border ${
                  day.isCurrentMonth 
                    ? 'bg-white border-gray-100' 
                    : 'bg-gray-50 border-transparent'
                } ${isToday ? 'ring-2 ring-[#E8650A]' : ''}`}
              >
                <div className={`text-sm font-medium mb-1 ${
                  day.isCurrentMonth ? 'text-gray-800' : 'text-gray-400'
                } ${isToday ? 'text-[#E8650A]' : ''}`}>
                  {day.date.getDate()}
                </div>
                
                {dayEvents.slice(0, 2).map((event, i) => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className={`text-[10px] px-1.5 py-0.5 rounded truncate cursor-pointer mb-0.5 ${
                      eventColors[i % eventColors.length]
                    } text-white`}
                  >
                    {event.summary}
                  </div>
                ))}
                
                {dayEvents.length > 2 && (
                  <div className="text-[10px] text-gray-500 px-1">
                    +{dayEvents.length - 2}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Liste des événements du mois */}
        <div className="mt-4 border-t border-gray-100 pt-4">
          <h3 className="font-semibold text-gray-800 mb-3">Prochains événements</h3>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucun événement ce mois-ci
            </div>
          ) : (
            <div className="space-y-2">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => setSelectedEvent(event)}
                  className="p-3 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors border border-gray-50"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-1 h-10 rounded-full ${eventColors[index % eventColors.length]}`} />
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm text-gray-800">{event.summary}</h4>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Clock size={12} />
                        {formatDate(event.start)}
                      </p>
                      {event.meet_link && (
                        <a
                          href={event.meet_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 mt-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium hover:bg-green-200"
                        >
                          <Video size={12} />
                          Meet
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Modal détail événement */}
      <AnimatePresence>
        {selectedEvent && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 md:left-[288px] bg-black/50 z-50"
              onClick={() => setSelectedEvent(null)}
            />
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[80vh] overflow-y-auto"
              style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-800">{selectedEvent.summary}</h2>
                  <button onClick={() => setSelectedEvent(null)} className="p-2 hover:bg-gray-100 rounded-full">
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-gray-600">
                    <Clock size={20} />
                    <span>{formatDate(selectedEvent.start)}</span>
                  </div>

                  {selectedEvent.location && (
                    <div className="flex items-center gap-3 text-gray-600">
                      <MapPin size={20} />
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}

                  {selectedEvent.attendees?.length > 0 && (
                    <div className="flex items-start gap-3 text-gray-600">
                      <Users size={20} className="mt-0.5" />
                      <div>
                        {selectedEvent.attendees.map((email, i) => (
                          <div key={i}>{email}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedEvent.description && (
                    <p className="text-gray-600 bg-gray-50 rounded-xl p-4">
                      {selectedEvent.description}
                    </p>
                  )}

                  {selectedEvent.meet_link && (
                    <a
                      href={selectedEvent.meet_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-4 bg-green-500 text-white rounded-2xl font-semibold hover:bg-green-600"
                    >
                      <Video size={20} />
                      Rejoindre Google Meet
                    </a>
                  )}

                  <button
                    onClick={() => deleteEvent(selectedEvent.id)}
                    className="w-full py-3 border-2 border-red-200 text-red-600 rounded-2xl font-medium hover:bg-red-50"
                  >
                    Supprimer l'événement
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modal création événement - Version mobile plein écran via Portal */}
      {showCreateModal && isMobile && createPortal(
        <AnimatePresence>
          <motion.div
            key="create-event-modal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed inset-0 z-[10002] flex flex-col bg-white"
          >
            {/* Header */}
            <header 
              className="flex items-center gap-2 px-3 border-b border-gray-100"
              style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
            >
              <button 
                onClick={() => setShowCreateModal(false)}
                className="p-1 text-gray-500"
              >
                <ArrowLeft size={22} />
              </button>
              <h1 className="text-[15px] font-bold text-gray-900">Nouvel événement</h1>
            </header>

              {/* Contenu scrollable */}
              <div className="flex-1 overflow-y-auto p-5">
                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Titre *</label>
                  <input
                    type="text"
                    value={newEvent.summary}
                    onChange={(e) => setNewEvent({ ...newEvent, summary: e.target.value })}
                    placeholder="Réunion d'équipe..."
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Date *</label>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Début</label>
                    <input
                      type="time"
                      value={newEvent.start_time}
                      onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Fin</label>
                    <input
                      type="time"
                      value={newEvent.end_time}
                      onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                      className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Participants</label>
                  <input
                    type="text"
                    value={newEvent.attendees}
                    onChange={(e) => setNewEvent({ ...newEvent, attendees: e.target.value })}
                    placeholder="marie@email.com, jean@email.com"
                    className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    placeholder="Détails de l'événement..."
                    rows={3}
                    className="w-full p-4 rounded-xl border text-[15px] resize-none bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>

                {/* Option Google Meet */}
                <div 
                  className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    newEvent.add_meet 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                  onClick={() => setNewEvent({ ...newEvent, add_meet: !newEvent.add_meet })}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      newEvent.add_meet ? 'border-green-500 bg-green-500' : 'border-gray-300'
                    }`}>
                      {newEvent.add_meet && <Check size={12} className="text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Video size={16} className={newEvent.add_meet ? 'text-green-600' : 'text-gray-400'} />
                        <span className="font-medium text-sm text-gray-800">Ajouter Google Meet</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer fixe */}
              <div 
                className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
                style={{ 
                  paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
                }}
              >
                <button
                  onClick={() => {
                    if (onAskAbra) {
                      onAskAbra({ title: newEvent.summary, date: newEvent.date, time: newEvent.time, location: newEvent.location, isNew: true });
                      setShowCreateModal(false);
                    } else {
                      toast.info('Demande à Abra de planifier ton événement !');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
                >
                  <Sparkles size={16} />
                  Créer avec Abracada
                </button>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={createEvent}
                    disabled={!newEvent.summary || !newEvent.date}
                    className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50"
                  >
                    Créer
                  </button>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>,
        document.body
      )}

      {/* Modal création événement - Version desktop centré */}
      <AnimatePresence>
        {showCreateModal && !isMobile && (
            /* ═══ VERSION DESKTOP ═══ */
            <div className="fixed inset-0 md:left-[288px] z-[10002] flex items-center justify-center p-4 bg-black/60">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900">Nouvel événement</h3>
                  <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                    <X size={20} className="text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Titre *</label>
                    <input
                      type="text"
                      value={newEvent.summary}
                      onChange={(e) => setNewEvent({ ...newEvent, summary: e.target.value })}
                      placeholder="Réunion d'équipe..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Date *</label>
                    <input
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Début</label>
                      <input
                        type="time"
                        value={newEvent.start_time}
                        onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Fin</label>
                      <input
                        type="time"
                        value={newEvent.end_time}
                        onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Participants (emails séparés par virgules)</label>
                    <input
                      type="text"
                      value={newEvent.attendees}
                      onChange={(e) => setNewEvent({ ...newEvent, attendees: e.target.value })}
                      placeholder="marie@email.com, jean@email.com"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Description (optionnel)</label>
                    <textarea
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                      placeholder="Détails de l'événement..."
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                    />
                  </div>

                  {/* Option Google Meet */}
                  <div 
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      newEvent.add_meet 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 bg-gray-50'
                    }`}
                    onClick={() => setNewEvent({ ...newEvent, add_meet: !newEvent.add_meet })}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        newEvent.add_meet ? 'border-green-500 bg-green-500' : 'border-gray-300'
                      }`}>
                        {newEvent.add_meet && <Check size={12} className="text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Video size={16} className={newEvent.add_meet ? 'text-green-600' : 'text-gray-400'} />
                          <span className="font-medium text-sm text-gray-800">Ajouter Google Meet</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                      Annuler
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        if (onAskAbra) {
                          onAskAbra({ title: newEvent.summary, date: newEvent.date, time: newEvent.time, location: newEvent.location, isNew: true });
                          setShowCreateModal(false);
                        } else {
                          toast.info('Demande à Abra de planifier ton événement !');
                        }
                      }}
                      className="px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2"
                      style={{ background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)' }}
                    >
                      <Sparkles size={16} />
                      Demander à Abra
                    </button>
                    <Button
                      onClick={createEvent}
                      disabled={!newEvent.summary || !newEvent.date}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      Créer l'événement
                    </Button>
                  </div>
                </div>
              </motion.div>
            </div>
        )}
      </AnimatePresence>

      {/* Modal d'inscription contextuel fallback (si parent ne gère pas) */}
      {!onOpenRegisterModal && (
        <RegisterPromptModal 
          isOpen={localShowRegisterModal} 
          onClose={() => setLocalShowRegisterModal(false)}
          actionLabel="synchroniser ton agenda Google"
          showGoogleOption={true}
        />
      )}
    </div>
  );
}
